import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap
} from 'rxjs';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { ErrorToastService } from '../error-toast.service';
import * as Sentry from '@sentry/angular';
import { DB_TEST_CONFIG } from './db-test-config.constants';
import {
  createBackupActionsTable,
  createDocumentsForUploadTable,
  createOrdersInEditStatusObject,
  createSyncActionsTable,
  dbConfig,
  ExpeditorDB,
  recreateOrdersObject
} from '../../db-description';
import { ModalController } from '@ionic/angular';
import { ActionsModalComponent } from '../../shared/actions-modal/actions-modal.component';

export enum HealthCheckStatus {
  CHECKING = 'checking',
  SUCCESS = 'success',
  FAILED = 'failed'
}

@Injectable({
  providedIn: 'root'
})
export class HealthCheckService {
  private readonly initCheckStatus = new BehaviorSubject<HealthCheckStatus>(
    HealthCheckStatus.CHECKING
  );

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public readonly initCheckStatus$ = this.initCheckStatus.asObservable();

  constructor(
    private readonly dbService: NgxIndexedDBService,
    private readonly errorToastService: ErrorToastService,
    private readonly modalCtrl: ModalController
  ) {}

  public checkOnInit(): Observable<boolean> {
    this.initCheckStatus.next(HealthCheckStatus.CHECKING);

    return this.checkDatabaseVersion().pipe(
      switchMap(versionOk => {
        if (!versionOk) {
          this.initCheckStatus.next(HealthCheckStatus.FAILED);
          return of(false);
        }

        return this.checkDatabaseTables().pipe(
          tap(result => {
            if (result) {
              this.initCheckStatus.next(HealthCheckStatus.SUCCESS);
            } else {
              this.initCheckStatus.next(HealthCheckStatus.FAILED);
              void this.showCleanupModal();
            }
          })
        );
      }),
      catchError(error => {
        this.initCheckStatus.next(HealthCheckStatus.FAILED);
        Sentry.captureException(error);
        void this.showCleanupModal();
        return of(false);
      })
    );
  }

  private checkDatabaseTables(): Observable<boolean> {
    try {
      const storesToCheck = Object.keys(DB_TEST_CONFIG);
      const tableChecks = storesToCheck.map(storeName =>
        this.checkTableReadWrite(storeName).pipe(
          catchError(error => {
            Sentry.captureException(error);
            return of(false);
          })
        )
      );

      return forkJoin(tableChecks).pipe(
        map(results => {
          const tableResults = storesToCheck.map((name, index) => ({
            name,
            result: results[index]
          }));

          return tableResults.every(item => item.result === true);
        }),
        catchError(error => {
          Sentry.captureException(error);
          return of(false);
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      return of(false);
    }
  }

  private checkTableReadWrite(storeName: string): Observable<boolean> {
    const tableConfig = DB_TEST_CONFIG[storeName];
    if (!tableConfig) {
      const error = new Error(
        `[HealthCheck] checkTableReadWrite: Конфигурация для таблицы ${storeName} не найдена`
      );
      Sentry.captureException(error);
      return of(false);
    }

    const testRecord = this.createTestRecord(storeName, tableConfig);

    return this.dbService.count(storeName).pipe(
      switchMap(() => this.dbService.add(storeName, testRecord)),
      switchMap(() => {
        const key = tableConfig.getKey(testRecord);
        return this.dbService.getByKey(storeName, key).pipe(
          catchError(error => {
            Sentry.captureException(error);
            this.cleanupTestRecord(storeName, key);
            throw error;
          })
        );
      }),
      switchMap(() => {
        const key = tableConfig.getKey(testRecord);
        return this.dbService.delete(storeName, key);
      }),
      map(() => true),
      catchError(error => {
        Sentry.captureException(error);

        if (
          error.name === 'NotFoundError' ||
          error.message?.includes('not found')
        ) {
          return this.createTable(storeName);
        }
        return of(false);
      })
    );
  }

  private createTestRecord(storeName: string, tableConfig: any): any {
    const testRecord = { ...tableConfig.testRecord };
    const testId = `test_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    if (testRecord.filter) {
      testRecord.filter = `${testRecord.filter}_${testId}`;
    }
    if (testRecord.idempotencyKey) {
      testRecord.idempotencyKey = `${testRecord.idempotencyKey}_${testId}`;
    }
    if (testRecord.orderNumber) {
      testRecord.orderNumber = `${testRecord.orderNumber}_${testId}`;
    }

    const currentUser = localStorage.getItem('login');

    if (currentUser) {
      if (storeName === 'SyncActionsTable') {
        testRecord.user = `${currentUser}_syncactions`;
      } else {
        testRecord.user = currentUser;
      }
    }

    return testRecord;
  }

  private cleanupTestRecord(storeName: string, key: any): void {
    this.dbService.delete(storeName, key).subscribe({
      error: error => {
        Sentry.captureException(error);
      }
    });
  }

  private createTable(storeName: string): Observable<boolean> {
    const tableConfig = DB_TEST_CONFIG[storeName];
    if (!tableConfig) {
      const error = new Error(
        `[HealthCheck] createTable: Конфигурация для таблицы ${storeName} не найдена`
      );
      Sentry.captureException(error);
      return of(false);
    }

    return new Observable<boolean>(observer => {
      try {
        const request = indexedDB.open(ExpeditorDB);

        request.onsuccess = event => {
          const db = (event.target as IDBOpenDBRequest).result;

          // Проверяем, существует ли таблица
          if (!db.objectStoreNames.contains(storeName)) {
            try {
              // Закрываем текущее соединение
              db.close();

              // Открываем базу данных с запросом на обновление версии
              const upgradeRequest = indexedDB.open(
                ExpeditorDB,
                db.version + 1
              );

              upgradeRequest.onupgradeneeded = upgradeEvent => {
                const upgradeDb = (upgradeEvent.target as IDBOpenDBRequest)
                  .result;

                // Создаем таблицу в зависимости от ее имени
                switch (storeName) {
                  case 'orders':
                    recreateOrdersObject(upgradeDb, 'orders');
                    break;
                  case 'ordersInWork':
                    recreateOrdersObject(upgradeDb, 'ordersInWork');
                    break;
                  case 'ordersCompleted':
                    recreateOrdersObject(upgradeDb, 'ordersCompleted');
                    break;
                  case 'ordersInEditStatus':
                    createOrdersInEditStatusObject(upgradeDb);
                    break;
                  case 'BackupActionsTable':
                    createBackupActionsTable(upgradeDb);
                    break;
                  case 'SyncActionsTable':
                    createSyncActionsTable(upgradeDb);
                    break;
                  case 'DocumentsForUpload':
                    createDocumentsForUploadTable(upgradeDb);
                    break;
                  default: {
                    const error = new Error(
                      `[HealthCheck] createTable: Неизвестная таблица ${storeName}`
                    );
                    Sentry.captureException(error);
                    break;
                  }
                }
              };

              upgradeRequest.onsuccess = () => {
                observer.next(true);
                observer.complete();
              };

              upgradeRequest.onerror = error => {
                Sentry.captureException(error);
                observer.next(false);
                observer.complete();
              };
            } catch (error) {
              Sentry.captureException(error);
              observer.next(false);
              observer.complete();
            }
          } else {
            observer.next(true);
            observer.complete();
          }
        };

        request.onerror = error => {
          Sentry.captureException(error);
          observer.next(false);
          observer.complete();
        };
      } catch (error) {
        Sentry.captureException(error);
        observer.next(false);
        observer.complete();
      }
    });
  }

  private checkDatabaseVersion(): Observable<boolean> {
    // Получаем ожидаемую версию из конфигурации
    const expectedVersion = dbConfig.version || 1; // Устанавливаем значение по умолчанию 1, если version не определен

    return new Observable<boolean>(observer => {
      try {
        // Открываем базу данных с помощью нативного API IndexedDB
        const request = indexedDB.open(ExpeditorDB);

        request.onsuccess = event => {
          const db = (event.target as IDBOpenDBRequest).result;
          const actualVersion = db.version;

          // Закрываем соединение с базой данных
          db.close();

          // Проверяем, что фактическая версия не больше ожидаемой
          if (actualVersion > expectedVersion) {
            this.showVersionMismatchModal(actualVersion, expectedVersion);
            observer.next(false);
          } else {
            observer.next(true);
          }
          observer.complete();
        };

        request.onerror = event => {
          Sentry.captureException(event);
          observer.next(false);
          observer.complete();
        };
      } catch (error) {
        Sentry.captureException(error);
        observer.next(false);
        observer.complete();
      }
    });
  }

  private async showVersionMismatchModal(
    actualVersion: number,
    expectedVersion: number
  ): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ActionsModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        data: {
          closeButton: true,
          message: `Версия базы данных (${actualVersion}) не соответствует ожидаемой (${expectedVersion}).
           Пожалуйста, обратитесь в техническую поддержку за помощью.`,
          title: 'Несоответствие версии базы данных',
          imageSrc: '../../../assets/images/error-picture.svg',
          errorTitle: true
        }
      }
    });

    await modal.present();
  }

  private async showCleanupModal(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ActionsModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        data: {
          closeButton: true,
          message: `Обнаружены проблемы с базой данных приложения.
           Рекомендуется очистить данные приложения и выполнить переустановку.
           Если после этого проблема не решится, пожалуйста, обратитесь в техническую поддержку за помощью.`,
          title: 'Проблема с базой данных',
          imageSrc: '../../../assets/images/error-picture.svg',
          errorTitle: true
        }
      }
    });

    await modal.present();
  }
}
